import React, { useCallback } from 'react';
import { useStore, Position, getSmoothStepPath } from 'reactflow';

const NODE_WIDTH = 250;
const CONNECTOR_OFFSET = 8; // Horizontal offset to prevent overlap when nodes are vertically aligned
const CONFIG = {
  label: {
    height: 20,
    padding: {
      x: 8,
      y: 4,
    },
    style: {
      fill: '#555555',
      fontWeight: 500,
      fontSize: 11,
      pointerEvents: 'none',
      fontFamily: 'Arial, sans-serif',
      dominantBaseline: 'middle',
      textAnchor: 'middle',
    },
    background: {
      fill: 'white',
      stroke: '#333333',
      strokeWidth: 1,
      rx: 4,
      ry: 4,
    },
  },
  path: {
    heightOffset: 120,
    style: {
      strokeWidth: 2,
      strokeDasharray: '5, 5',
      stroke: 'rgba(134, 134, 134, 0.4)',
    },
  },
};

const ArrowMarker = ({ markerId }) => (
  <marker
    id={markerId}
    viewBox="0 0 10 10"
    refX="8"
    refY="5"
    markerWidth="8"
    markerHeight="8"
    orient="auto"
  >
    <path d="M 0 0 L 10 5 L 0 10 z" fill="#868686" />
  </marker>
);

const DashAnimation = () => (
  <style>
    {`
      @keyframes dashFlow {
        from { stroke-dashoffset: 20; }
        to { stroke-dashoffset: 0; }
      }
      .animated-dash {
        animation: dashFlow 0.8s linear infinite;
      }
    `}
  </style>
);

const EdgeLabel = ({ centerX, centerY, textWidth, labelText }) => (
  <g transform={`translate(${centerX}, ${centerY})`} className="react-flow__edge-text">
    <rect
      x={-(textWidth + CONFIG.label.padding.x) / 2}
      y={-(CONFIG.label.height + CONFIG.label.padding.y) / 2}
      width={textWidth + CONFIG.label.padding.x}
      height={CONFIG.label.height + CONFIG.label.padding.y}
      style={CONFIG.label.background}
    />
    <text style={CONFIG.label.style}>{labelText}</text>
  </g>
);

const SmoothStepDottedLink = ({ id, source, target, labelText }) => {
  const sourceNode = useStore(useCallback(store => store.nodeInternals.get(source), [source]));
  const targetNode = useStore(useCallback(store => store.nodeInternals.get(target), [target]));

  if (!sourceNode || !targetNode) return null;

  const markerId = `arrowhead-${id}`;
  const markerEnd = `url('#${markerId}')`;

  // Offset connectors horizontally: source (out) to the right, target (in) to the left
  // This prevents overlap when nodes are vertically aligned
  const sourceX = sourceNode.position.x + NODE_WIDTH / 2 + CONNECTOR_OFFSET;
  const targetX = targetNode.position.x + NODE_WIDTH / 2 - CONNECTOR_OFFSET;

  const edgePath = getSmoothStepPath({
    sourceX,
    sourceY: sourceNode.position.y,
    sourcePosition: Position.Top,
    targetPosition: Position.Top,
    targetX,
    targetY: targetNode.position.y,
    offset: CONFIG.path.heightOffset,
  });

  if (!edgePath?.length) return null;

  const centerX = (sourceX + targetX) / 2;
  const minY = Math.min(sourceNode.position.y, targetNode.position.y);
  const centerY = minY - CONFIG.path.heightOffset;
  const textWidth = labelText.length * 6;

  return (
    <>
      <defs>
        <ArrowMarker markerId={markerId} />
        <DashAnimation />
      </defs>

      <path
        id={id}
        className="react-flow__edge-path animated-dash"
        d={edgePath[0]}
        markerEnd={markerEnd}
        style={CONFIG.path.style}
      />

      <EdgeLabel centerX={centerX} centerY={centerY} textWidth={textWidth} labelText={labelText} />
    </>
  );
};

export default SmoothStepDottedLink;
